import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  Box,
  Typography,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountTree as AuditUniverseIcon,
  Security as RiskIcon,
  AccountBalance as ProcessIcon,
  CalendarMonth as PlanIcon,
  BarChart as GanttIcon,
  Assignment as EngagementIcon,
  ReportProblem as FindingIcon,
  Assessment as ReportIcon,
  TrackChanges as MeasureIcon,
  Gavel as CharterIcon,
  VerifiedUser as IndependenceIcon,
  Fingerprint as SealIcon,
  PrivacyTip as PrivacyIcon,
  Balance as EthicsIcon,
  School as CompetenceIcon,
  HighQuality as QAIPIcon,
  TrendingUp as StrategyIcon,
  MenuBook as MethodsIcon,
  Savings as ResourcesIcon,
  Insights as PerformanceIcon,
  HowToVote as ApprovalIcon,
  PersonOutline as UserIcon,
  AdminPanelSettings as RoleIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { usePermissions } from '@/hooks/usePermissions';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 72;

interface SidebarProps {
  open: boolean;
  collapsed: boolean;
  onClose?: () => void;
  variant?: 'permanent' | 'temporary';
}

interface NavSection {
  key: string;
  labelKey: string;
  icon: React.ReactNode;
  items: NavItem[];
  /**
   * Rechte, von denen eines genügt.
   *
   * Der Abschnitt Administration hing allein an den Rollennamen SUPER_ADMIN
   * und TENANT_ADMIN. Die Rollen eines Mandanten heißen aber deutsch —
   * Administrator, Revisionsleitung —, also war der Menüpunkt für jedes Konto
   * unsichtbar, auch für eines mit allen 75 Rechten. Gefiltert wird jetzt nach
   * denselben Rechten, die auch die Seiten dahinter verlangen.
   */
  requiredPermissions?: string[];
}

interface NavItem {
  key: string;
  labelKey: string;
  icon: React.ReactNode;
  path: string;
  /** Ein Recht — oder eine Liste, von der eines genügt. */
  requiredPermission?: string | string[];
}

const navSections: NavSection[] = [
  {
    key: 'dashboard',
    labelKey: 'nav.dashboard',
    icon: <DashboardIcon />,
    items: [
      { key: 'dashboard', labelKey: 'nav.dashboard', icon: <DashboardIcon />, path: '/' },
    ],
  },
  {
    key: 'preparation',
    labelKey: 'nav.auditPreparation',
    icon: <AuditUniverseIcon />,
    items: [
      { key: 'auditUniverse', labelKey: 'nav.auditUniverse', icon: <AuditUniverseIcon />, path: '/audit-universe', requiredPermission: 'READ:audit_universe' },
      { key: 'riskModels', labelKey: 'nav.riskAssessment', icon: <RiskIcon />, path: '/risk/models', requiredPermission: 'READ:risk' },
      { key: 'riskMatrix', labelKey: 'Risikomatrix', icon: <RiskIcon />, path: '/risk/matrix', requiredPermission: 'READ:risk' },
      { key: 'riskOverall', labelKey: 'Gesamt-Risikobewertung', icon: <RiskIcon />, path: '/risk/overall', requiredPermission: 'READ:risk' },
      { key: 'businessProcesses', labelKey: 'nav.businessProcesses', icon: <ProcessIcon />, path: '/business-processes', requiredPermission: 'READ:audit_universe' },
    ],
  },
  {
    key: 'planning',
    labelKey: 'nav.auditPlanning',
    icon: <PlanIcon />,
    items: [
      { key: 'planning', labelKey: 'nav.multiYearPlan', icon: <PlanIcon />, path: '/planning', requiredPermission: 'READ:plan' },
      { key: 'multiYear', labelKey: 'Mehrjahresplan (Turnus)', icon: <PlanIcon />, path: '/planning/multi-year', requiredPermission: 'READ:plan' },
      { key: 'cockpit', labelKey: 'Planungs-Cockpit', icon: <DashboardIcon />, path: '/planning/cockpit', requiredPermission: 'READ:plan' },
      { key: 'gantt', labelKey: 'nav.gantt', icon: <GanttIcon />, path: '/planning/gantt', requiredPermission: 'READ:plan' },
    ],
  },
  {
    key: 'execution',
    labelKey: 'nav.auditExecution',
    icon: <EngagementIcon />,
    items: [
      { key: 'engagements', labelKey: 'nav.engagements', icon: <EngagementIcon />, path: '/engagements', requiredPermission: 'READ:engagement' },
      { key: 'findings', labelKey: 'nav.findings', icon: <FindingIcon />, path: '/findings', requiredPermission: 'READ:finding' },
    ],
  },
  {
    key: 'reporting',
    labelKey: 'nav.reporting',
    icon: <ReportIcon />,
    items: [
      { key: 'reports', labelKey: 'nav.reports', icon: <ReportIcon />, path: '/reports', requiredPermission: 'READ:report' },
      { key: 'annualReports', labelKey: 'nav.annualReports', icon: <ReportIcon />, path: '/annual-reports', requiredPermission: 'READ:report' },
    ],
  },
  {
    key: 'followUp',
    labelKey: 'nav.followUp',
    icon: <MeasureIcon />,
    items: [
      { key: 'measures', labelKey: 'nav.measures', icon: <MeasureIcon />, path: '/measures', requiredPermission: 'READ:measure' },
    ],
  },
  {
    key: 'governance',
    labelKey: 'nav.governance',
    icon: <CharterIcon />,
    items: [
      { key: 'charter', labelKey: 'nav.charter', icon: <CharterIcon />, path: '/governance/charter', requiredPermission: 'READ:governance' },
      // Die eigene Unabhängigkeitserklärung gibt auch ab, wer die übrige
      // Governance nicht lesen darf.
      { key: 'independence', labelKey: 'nav.independence', icon: <IndependenceIcon />, path: '/governance/independence', requiredPermission: ['READ:governance', 'READ:independence_declaration'] },
      { key: 'ethics', labelKey: 'nav.ethics', icon: <EthicsIcon />, path: '/governance/ethics', requiredPermission: 'READ:governance' },
      { key: 'competence', labelKey: 'nav.competence', icon: <CompetenceIcon />, path: '/governance/competence', requiredPermission: 'READ:governance' },
      { key: 'qaip', labelKey: 'nav.qaip', icon: <QAIPIcon />, path: '/governance/qaip', requiredPermission: 'READ:governance' },
      { key: 'strategy', labelKey: 'nav.strategy', icon: <StrategyIcon />, path: '/governance/strategy', requiredPermission: 'READ:governance' },
      { key: 'methods', labelKey: 'nav.methods', icon: <MethodsIcon />, path: '/governance/methods', requiredPermission: 'READ:governance' },
      { key: 'resources', labelKey: 'nav.resourcePlan', icon: <ResourcesIcon />, path: '/governance/resources', requiredPermission: 'READ:governance' },
      { key: 'performance', labelKey: 'nav.performance', icon: <PerformanceIcon />, path: '/governance/performance', requiredPermission: 'READ:governance' },
      { key: 'conformity', labelKey: 'nav.conformity', icon: <QAIPIcon />, path: '/conformity', requiredPermission: 'READ:conformity' },
      { key: 'snapshots', labelKey: 'nav.snapshots', icon: <QAIPIcon />, path: '/conformity/snapshots', requiredPermission: 'READ:conformity' },
      { key: 'registers', labelKey: 'nav.registers', icon: <CharterIcon />, path: '/conformity/registers', requiredPermission: 'READ:conformity' },
      { key: 'approval', labelKey: 'nav.approvalPackage', icon: <ApprovalIcon />, path: '/conformity/approval', requiredPermission: 'READ:conformity' },
      // Stakeholder page not yet implemented
    ],
  },
  {
    key: 'administration',
    labelKey: 'nav.administration',
    icon: <SettingsIcon />,
    // READ:user steht hier nicht mehr: Die Benutzerliste darf seit der
    // Rechtekorrektur jede Rolle lesen, damit auf den Seiten Namen statt
    // Kennungen stehen. Als Eintrittskarte in die Verwaltung taugt das nicht.
    requiredPermissions: ['UPDATE:user', 'READ:role', 'UPDATE:tenant', 'READ:data_protection', 'READ:audit_trail'],
    items: [
      // Tenants page is Super-Admin only, not in regular menu
      // Dieselben Rechte wie die Seite (UsersPage): verwalten oder die Rechte
      // prüfen. READ:role allein öffnet sie nur lesend — für die
      // Rezertifizierung durch Revisionsleitung und QS (TG 2.3, 4.2 Nr. 1).
      { key: 'users', labelKey: 'nav.users', icon: <UserIcon />, path: '/admin/users', requiredPermission: ['UPDATE:user', 'READ:role'] },
      { key: 'roles', labelKey: 'nav.roles', icon: <RoleIcon />, path: '/admin/roles', requiredPermission: 'READ:role' },
      { key: 'settings', labelKey: 'nav.settings', icon: <SettingsIcon />, path: '/admin/settings', requiredPermission: 'UPDATE:tenant' },
      // Eigenes Recht seit Migration 36 (S-05) — READ:governance genügt im
      // core-service nicht mehr.
      { key: 'auditTrail', labelKey: 'nav.auditTrail', icon: <SealIcon />, path: '/admin/audit-trail', requiredPermission: 'READ:audit_trail' },
      { key: 'dataProtection', labelKey: 'nav.dataProtection', icon: <PrivacyIcon />, path: '/admin/data-protection', requiredPermission: 'READ:data_protection' },
    ],
  },
];

/**
 * Jeder Eintrag trägt das Recht, das an seinen Endpunkten steht.
 *
 * Vorher trug fast keiner eines — und das fiel nicht auf, weil auch die
 * Endpunkte keines verlangten: Wer sich anmelden konnte, sah alles und durfte
 * alles lesen. Seit die Endpunkte prüfen, wäre ein ungeschützter Menüpunkt ein
 * Versprechen, das die Seite dahinter nicht hält: Der Revisor klickte auf
 * „Charta" und bekäme eine Fehlermeldung statt eines Textes.
 */
export const Sidebar: React.FC<SidebarProps> = ({ open, collapsed, onClose, variant = 'permanent' }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, hasAnyPermission } = usePermissions();

  /**
   * Ob ein Abschnitt gezeigt wird: eines der genannten Rechte genügt. Ohne
   * genannte Rechte steht kein Tor davor.
   *
   * Ein Rollenname öffnet nichts mehr. Hier stach früher eine „Plattformrolle"
   * SUPER_ADMIN/TENANT_ADMIN — Rollennamen vergibt aber der Mandant, und der
   * Dienst prüft seit S-02 (GAP-Analyse 03.10.2026) nur noch Rechte.
   */
  const itemVisible = (item: NavItem): boolean => {
    const recht = item.requiredPermission;
    if (!recht) return true;
    return Array.isArray(recht) ? hasAnyPermission(recht) : hasPermission(recht);
  };

  const sectionVisible = (section: NavSection): boolean => {
    // Ein Abschnitt, dessen Einträge alle verborgen sind, ist eine leere
    // Überschrift: Der Revisor sah „Governance", klappte es auf und fand
    // nichts. Hat der Abschnitt Einträge, entscheidet, ob einer davon bleibt.
    const eintraege = section.items ?? [];
    if (eintraege.length > 0) {
      const sichtbar = eintraege.some(itemVisible);
      if (!sichtbar) return false;
    }
    if (!section.requiredPermissions) return true;
    if (section.requiredPermissions && hasAnyPermission(section.requiredPermissions)) return true;
    return false;
  };
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    dashboard: true,
  });

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const drawerWidth = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: collapsed ? 1 : 2 }}>
        {!collapsed && (
          <Typography variant="h6" noWrap color="primary" fontWeight={700}>
            {t('app.title')}
          </Typography>
        )}
      </Toolbar>
      <Divider />
      <Box sx={{ overflow: 'auto', flex: 1, py: 1 }}>
        {navSections.map((section) => {
          if (!sectionVisible(section)) {
            return null;
          }

          // Single-item sections (like Dashboard) render directly
          if (section.items.length === 1) {
            const item = section.items[0];
            return (
              <ListItemButton
                key={item.key}
                selected={isActive(item.path)}
                onClick={() => navigate(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  mb: 0.5,
                  minHeight: 44,
                  justifyContent: collapsed ? 'center' : 'initial',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 40,
                    color: isActive(item.path) ? 'primary.main' : 'inherit',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={t(item.labelKey)} />}
              </ListItemButton>
            );
          }

          return (
            <React.Fragment key={section.key}>
              <ListItemButton
                onClick={() => toggleSection(section.key)}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  mb: 0.5,
                  minHeight: 44,
                  justifyContent: collapsed ? 'center' : 'initial',
                }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40 }}>
                  {section.icon}
                </ListItemIcon>
                {!collapsed && (
                  <>
                    <ListItemText
                      primary={t(section.labelKey)}
                      primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                    />
                    {expandedSections[section.key] ? <ExpandLess /> : <ExpandMore />}
                  </>
                )}
              </ListItemButton>
              {!collapsed && (
                <Collapse in={expandedSections[section.key]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {section.items.map((item) => {
                      if (!itemVisible(item)) {
                        return null;
                      }
                      return (
                        <ListItemButton
                          key={item.key}
                          selected={isActive(item.path)}
                          onClick={() => navigate(item.path)}
                          sx={{
                            pl: 4,
                            mx: 1,
                            borderRadius: 1,
                            mb: 0.25,
                            minHeight: 38,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 32,
                              color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                              '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={t(item.labelKey)}
                            primaryTypographyProps={{ fontSize: '0.8125rem' }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </Box>
      <Divider />
      <Box sx={{ p: 2, textAlign: 'center' }}>
        {!collapsed && (
          // Mit Build-Kennung: Zwei Oberflächen derselben Versionsnummer
          // sollen zu unterscheiden sein (Testgrundlage 2.2, B-01).
          <Typography variant="caption" color="text.secondary" data-testid="app-version">
            {t('common.version')} {__APP_VERSION__} · Build {__APP_BUILD__}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};
